/**
 * Fuente de tarjetas guardadas. Implementación productiva: `GET /v2/order/checkout/payment/card`
 * (middleware de pagos). Detrás de una interfaz para no acoplar application/UI al transporte.
 */
import type { SavedCard } from '../domain/models';
import type { PaymentMiddlewareClient } from './paymentMiddlewareClient';

export interface PaymentOptions {
  cards: SavedCard[];
  /** El GET trae `MERCADOPAGO` con `enabled: true`: el banner de Mercado Pago se muestra. */
  mercadoPagoEnabled: boolean;
}

export interface SavedCardsSource {
  getSavedCards(): Promise<SavedCard[]>;
  getPaymentOptions(): Promise<PaymentOptions>;
}

export const createStaticSavedCardsSource = (
  cards: SavedCard[],
  mercadoPagoEnabled = false,
): SavedCardsSource => ({
  getSavedCards: async () => cards,
  getPaymentOptions: async () => ({ cards, mercadoPagoEnabled }),
});

/**
 * Tarjetas del proveedor PAYWAY habilitado y si Mercado Pago está habilitado. Soft-fail (igual que el
 * backend): si la consulta falla se devuelve lista vacía y sin Mercado Pago, así el usuario puede
 * igualmente agregar una tarjeta nueva.
 */
export const createMiddlewareSavedCardsSource = (
  client: Pick<PaymentMiddlewareClient, 'getSavedCards'>,
): SavedCardsSource => {
  const getPaymentOptions = async (): Promise<PaymentOptions> => {
    try {
      const providers = await client.getSavedCards();
      return {
        cards: providers
          .filter(({ provider, enabled }) => provider === 'PAYWAY' && enabled)
          .flatMap(({ cards }) => cards),
        mercadoPagoEnabled: providers.some(
          ({ provider, enabled }) => provider === 'MERCADOPAGO' && enabled,
        ),
      };
    } catch {
      return { cards: [], mercadoPagoEnabled: false };
    }
  };
  return {
    getPaymentOptions,
    getSavedCards: async () => (await getPaymentOptions()).cards,
  };
};
